import { useMemo } from "react";
import type { ParkingDetailsFormData } from "../../PermitContextProvider/types";
import type { PermitTypeDto } from "../../types";
import { getDefaultVehicleState } from "./get-default-vehicle-state";
import { getRememberedInfo } from "./remembered-info";

type UseRememberedInfoParams = {
	initialValues?: Partial<ParkingDetailsFormData>;
	isEmailRequired: boolean;
	isKiosk: boolean;
	isLoggedIn: boolean;
	isSmsEnabled: boolean;
	isSmsRequired: boolean;
	permitType: PermitTypeDto;
};

export const useRememberedInfo = ({
	initialValues,
	isEmailRequired,
	isKiosk,
	isLoggedIn,
	isSmsEnabled,
	isSmsRequired,
	permitType,
}: UseRememberedInfoParams) => {
	const rememberedInfo = useMemo(() => (isLoggedIn ? null : getRememberedInfo()), [isLoggedIn]);
	const hasRememberedInfo = !!rememberedInfo;

	const getInitialShouldSendEmail = () => {
		if (isEmailRequired) {
			return true;
		}
		if (initialValues?.email !== undefined) {
			return !!initialValues.email;
		}
		if (isKiosk) {
			return false;
		}
		if (hasRememberedInfo) {
			return !!rememberedInfo?.email;
		}
		return true;
	};

	const getInitialShouldSendSms = () => {
		if (!isSmsEnabled) {
			return false;
		}
		if (isSmsRequired) {
			return true;
		}
		if (initialValues?.phoneNumber !== undefined) {
			return !!initialValues.phoneNumber;
		}
		if (hasRememberedInfo) {
			return !!rememberedInfo?.phoneNumber;
		}
		return false;
	};

	const getInitialVehicles = () => {
		if (initialValues?.vehicles?.length) {
			return initialValues.vehicles;
		}

		if (isLoggedIn) {
			return [];
		}

		const rememberedVehicles = rememberedInfo?.vehicles ?? [];

		if (rememberedVehicles.length === 0) {
			return [
				{
					plate: "",
					state: getDefaultVehicleState({ permitType }),
				},
			];
		}

		const maxVehicles = permitType.maxVehicles ?? 1;
		return rememberedVehicles.slice(0, maxVehicles);
	};

	return {
		initialFormValues: {
			shouldSendEmail: getInitialShouldSendEmail(),
			email: initialValues?.email ?? (isKiosk ? "" : (rememberedInfo?.email ?? "")),
			shouldSendSms: getInitialShouldSendSms(),
			phoneNumber: isSmsEnabled ? (initialValues?.phoneNumber ?? rememberedInfo?.phoneNumber ?? "") : "",
			vehicles: getInitialVehicles(),
			rememberInfo: hasRememberedInfo,
		},
	};
};
